import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, timer } from 'rxjs';
import { tap, shareReplay, take } from 'rxjs/operators';

/**
 * Cache entry interface
 */
export interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  tags?: string[];
  size?: number;
}

/**
 * Cache configuration interface
 */
export interface CacheConfig {
  /** Default TTL in milliseconds */
  defaultTTL: number;
  /** Maximum cache size (number of entries) */
  maxSize: number;
  /** Maximum memory usage in MB */
  maxMemoryMB: number;
  /** Enable automatic cleanup */
  enableAutoCleanup: boolean;
  /** Cleanup interval in milliseconds */
  cleanupInterval: number;
  /** Enable cache statistics */
  enableStats: boolean;
}

/**
 * Cache statistics interface
 */
export interface CacheStats {
  totalEntries: number;
  totalHits: number;
  totalMisses: number;
  hitRate: number;
  memoryUsageMB: number;
  oldestEntry: Date | null;
  newestEntry: Date | null;
  topKeys: { key: string; accessCount: number }[];
  tagBreakdown: { [tag: string]: number };
}

/**
 * Export payload shape returned by `exportCache()` / consumed by `importCache()`.
 */
export interface CacheExportEntry<T = unknown> {
  key: string;
  data: T;
  timestamp: number;
  ttl: number;
  accessCount: number;
  lastAccessed: number;
  tags?: string[];
}

export interface CacheExport {
  entries: CacheExportEntry[];
  stats: CacheStats;
  config: CacheConfig;
  exportedAt: number;
}

/**
 * Cache eviction strategy
 */
export enum EvictionStrategy {
  LRU = 'lru', // Least Recently Used
  LFU = 'lfu', // Least Frequently Used
  FIFO = 'fifo', // First In, First Out
  TTL = 'ttl' // Time To Live based
}

/**
 * @domain Core/Services/Shared
 * 
 * Advanced caching service with multiple eviction strategies and monitoring.
 * 
 * This service provides:
 * - Multiple eviction strategies (LRU, LFU, FIFO, TTL)
 * - Tag-based cache invalidation
 * - Memory usage monitoring
 * - Cache statistics and analytics
 * - Automatic cleanup and maintenance
 * - Observable-based caching for reactive applications
 * 
 * Usage:
 * ```typescript
 * // Cache a value
 * this.caching.set('user:123', userData, 300000); // 5 minutes TTL
 * 
 * // Get a cached value
 * const user = this.caching.get<User>('user:123');
 * 
 * // Cache an observable
 * const users$ = this.caching.cacheObservable(
 *   'users:all',
 *   this.userService.getUsers(),
 *   600000 // 10 minutes TTL
 * );
 * 
 * // Invalidate by tag
 * this.caching.invalidateByTag('users');
 * 
 * // Get cache statistics
 * const stats = this.caching.getStats();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class CachingService {
  private cache = new Map<string, CacheEntry<unknown>>();
  private observableCache = new Map<string, Observable<unknown>>();
  private cleanupTimer?: ReturnType<typeof setInterval>;
  
  // Statistics
  private hits = 0;
  private misses = 0;
  
  // Observables for monitoring
  private statsSubject = new BehaviorSubject<CacheStats>(this.calculateStats());
  public stats$ = this.statsSubject.asObservable();

  // Default configuration
  private config: CacheConfig = {
    defaultTTL: 300000, // 5 minutes
    maxSize: 1000,
    maxMemoryMB: 50,
    enableAutoCleanup: true,
    cleanupInterval: 60000, // 1 minute
    enableStats: true
  };

  constructor() {
    this.startAutoCleanup();
  }

  // ========================================
  // BASIC CACHE OPERATIONS
  // ========================================

  /**
   * Set a value in the cache
   */
  set<T>(key: string, value: T, ttl?: number, tags?: string[]): void {
    const entry: CacheEntry<T> = {
      key,
      data: value,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
      accessCount: 0,
      lastAccessed: Date.now(),
      tags,
      size: this.estimateSize(value)
    };

    // Check if we need to evict entries
    if (this.cache.size >= this.config.maxSize) {
      this.evictEntries(1);
    }

    this.cache.set(key, entry);
    this.updateStats();
    
    console.log(`💾 Cache SET: ${key} (TTL: ${entry.ttl}ms, Tags: ${tags?.join(', ') || 'none'})`);
  }

  /**
   * Get a value from the cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;
    
    if (!entry) {
      this.misses++;
      this.updateStats();
      return null;
    }

    // Check if entry has expired
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      this.misses++;
      this.updateStats();
      return null;
    }

    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    this.hits++;
    this.updateStats();
    
    return entry.data;
  }

  /**
   * Check if a key exists in the cache
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    return entry !== undefined && !this.isExpired(entry);
  }

  /**
   * Delete a specific key from the cache
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.updateStats();
      console.log(`🗑️ Cache DELETE: ${key}`);
    }
    return deleted;
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
    this.observableCache.clear();
    this.hits = 0;
    this.misses = 0;
    this.updateStats();
    console.log('🧹 Cache CLEARED');
  }

  // ========================================
  // OBSERVABLE CACHING
  // ========================================

  /**
   * Cache an observable with automatic invalidation
   */
  cacheObservable<T>(
    key: string, 
    source: Observable<T>, 
    ttl?: number, 
    tags?: string[]
  ): Observable<T> {
    // Check if we already have this observable cached
    if (this.observableCache.has(key)) {
      const cachedData = this.get<T>(key);
      if (cachedData !== null) {
        console.log(`🔄 Observable cache HIT: ${key}`);
        return of(cachedData);
      }
    }

    // Create new cached observable
    const cached$ = source.pipe(
      tap(data => {
        this.set(key, data, ttl, tags);
        console.log(`💾 Observable cached: ${key}`);
      }),
      shareReplay(1)
    );

    this.observableCache.set(key, cached$);

    // Set up automatic cleanup
    if (ttl) {
      timer(ttl).pipe(take(1)).subscribe(() => {
        this.observableCache.delete(key);
      });
    }

    return cached$;
  }

  /**
   * Get or create a cached observable
   */
  getOrSetObservable<T>(
    key: string,
    factory: () => Observable<T>,
    ttl?: number,
    tags?: string[]
  ): Observable<T> {
    const cachedData = this.get<T>(key);
    if (cachedData !== null) {
      return of(cachedData);
    }

    return this.cacheObservable(key, factory(), ttl, tags);
  }

  // ========================================
  // ADVANCED CACHE OPERATIONS
  // ========================================

  /**
   * Invalidate cache entries by tag
   */
  invalidateByTag(tag: string): number {
    let invalidated = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.tags && entry.tags.includes(tag)) {
        this.cache.delete(key);
        this.observableCache.delete(key);
        invalidated++;
      }
    }

    if (invalidated > 0) {
      this.updateStats();
      console.log(`🏷️ Cache invalidated by tag '${tag}': ${invalidated} entries`);
    }

    return invalidated;
  }

  /**
   * Invalidate cache entries by pattern
   */
  invalidateByPattern(pattern: RegExp): number {
    let invalidated = 0;
    
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key);
        this.observableCache.delete(key);
        invalidated++;
      }
    }

    if (invalidated > 0) {
      this.updateStats();
      console.log(`🔍 Cache invalidated by pattern: ${invalidated} entries`);
    }

    return invalidated;
  }

  /**
   * Refresh a cached entry
   */
  refresh<T>(key: string, factory: () => Observable<T>, ttl?: number, tags?: string[]): Observable<T> {
    this.delete(key);
    this.observableCache.delete(key);
    return this.cacheObservable(key, factory(), ttl, tags);
  }

  /**
   * Extend TTL of a cached entry
   */
  extendTTL(key: string, additionalTime: number): boolean {
    const entry = this.cache.get(key);
    if (entry && !this.isExpired(entry)) {
      entry.ttl += additionalTime;
      return true;
    }
    return false;
  }

  /**
   * Get multiple values at once
   */
  getMultiple<T>(keys: string[]): { [key: string]: T | null } {
    const result: { [key: string]: T | null } = {};
    
    keys.forEach(key => {
      result[key] = this.get<T>(key);
    });

    return result;
  }

  /**
   * Set multiple values at once
   */
  setMultiple<T>(entries: { key: string; value: T; ttl?: number; tags?: string[] }[]): void {
    entries.forEach(entry => {
      this.set(entry.key, entry.value, entry.ttl, entry.tags);
    });
  }

  // ========================================
  // EVICTION STRATEGIES
  // ========================================

  /**
   * Evict entries based on strategy
   */
  private evictEntries(count: number, strategy: EvictionStrategy = EvictionStrategy.LRU): void {
    const entries = Array.from(this.cache.entries());
    let toEvict: string[] = [];

    switch (strategy) {
      case EvictionStrategy.LRU:
        toEvict = entries
          .sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed)
          .slice(0, count)
          .map(([key]) => key);
        break;

      case EvictionStrategy.LFU:
        toEvict = entries
          .sort(([, a], [, b]) => a.accessCount - b.accessCount)
          .slice(0, count)
          .map(([key]) => key);
        break;

      case EvictionStrategy.FIFO:
        toEvict = entries
          .sort(([, a], [, b]) => a.timestamp - b.timestamp)
          .slice(0, count)
          .map(([key]) => key);
        break;

      case EvictionStrategy.TTL:
        toEvict = entries
          .filter(([, entry]) => this.isExpired(entry))
          .slice(0, count)
          .map(([key]) => key);
        break;
    }

    toEvict.forEach(key => {
      this.cache.delete(key);
      this.observableCache.delete(key);
    });

    if (toEvict.length > 0) {
      console.log(`🗑️ Cache evicted ${toEvict.length} entries using ${strategy} strategy`);
      this.updateStats();
    }
  }

  /**
   * Cleanup expired entries
   */
  private cleanupExpired(): void {
    let cleaned = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        this.observableCache.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log(`🧹 Cache cleanup: removed ${cleaned} expired entries`);
      this.updateStats();
    }
  }

  // ========================================
  // STATISTICS & MONITORING
  // ========================================

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return this.calculateStats();
  }

  /**
   * Calculate current cache statistics
   */
  private calculateStats(): CacheStats {
    const entries = Array.from(this.cache.values());
    const totalEntries = entries.length;
    const totalRequests = this.hits + this.misses;
    const hitRate = totalRequests > 0 ? (this.hits / totalRequests) * 100 : 0;

    // Memory usage estimation
    const memoryUsageMB = entries.reduce((sum, entry) => sum + (entry.size || 0), 0) / (1024 * 1024);

    // Oldest and newest entries
    const timestamps = entries.map(e => e.timestamp);
    const oldestEntry = timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null;
    const newestEntry = timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null;

    // Top accessed keys
    const topKeys = entries
      .sort((a, b) => b.accessCount - a.accessCount)
      .slice(0, 10)
      .map(entry => ({ key: entry.key, accessCount: entry.accessCount }));

    // Tag breakdown
    const tagBreakdown: { [tag: string]: number } = {};
    entries.forEach(entry => {
      if (entry.tags) {
        entry.tags.forEach(tag => {
          tagBreakdown[tag] = (tagBreakdown[tag] || 0) + 1;
        });
      }
    });

    return {
      totalEntries,
      totalHits: this.hits,
      totalMisses: this.misses,
      hitRate: Math.round(hitRate * 100) / 100,
      memoryUsageMB: Math.round(memoryUsageMB * 100) / 100,
      oldestEntry,
      newestEntry,
      topKeys,
      tagBreakdown
    };
  }

  /**
   * Update statistics observable
   */
  private updateStats(): void {
    if (this.config.enableStats) {
      this.statsSubject.next(this.calculateStats());
    }
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.hits = 0;
    this.misses = 0;
    this.updateStats();
    console.log('📊 Cache statistics reset');
  }

  // ========================================
  // CONFIGURATION & UTILITIES
  // ========================================

  /**
   * Update cache configuration
   */
  updateConfig(newConfig: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart auto cleanup if interval changed
    if (newConfig.cleanupInterval !== undefined) {
      this.stopAutoCleanup();
      this.startAutoCleanup();
    }

  }

  /**
   * Get current configuration
   */
  getConfig(): CacheConfig {
    return { ...this.config };
  }

  /**
   * Export cache data
   */
  exportCache(): CacheExport {
    const entries: CacheExportEntry[] = [];
    
    for (const [key, entry] of this.cache.entries()) {
      entries.push({
        key,
        data: entry.data,
        timestamp: entry.timestamp,
        ttl: entry.ttl,
        accessCount: entry.accessCount,
        lastAccessed: entry.lastAccessed,
        tags: entry.tags
      });
    }

    return {
      entries,
      stats: this.getStats(),
      config: this.config,
      exportedAt: Date.now()
    };
  }

  /**
   * Import cache data
   */
  importCache(data: CacheExport): void {
    this.clear();

    if (data.entries) {
      data.entries.forEach(entry => {
        this.cache.set(entry.key, {
          ...entry,
          size: this.estimateSize(entry.data)
        });
      });
    }

    if (data.config) {
      this.updateConfig(data.config);
    }

    this.updateStats();
    console.log('📥 Cache data imported');
  }

  // ========================================
  // PRIVATE HELPER METHODS
  // ========================================

  /**
   * Check if a cache entry has expired
   */
  private isExpired(entry: CacheEntry<unknown>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  /**
   * Estimate the size of a value in bytes
   */
  private estimateSize(value: unknown): number {
    try {
      return new Blob([JSON.stringify(value)]).size;
    } catch {
      return 1024; // Default estimate
    }
  }

  /**
   * Start automatic cleanup
   */
  private startAutoCleanup(): void {
    if (this.config.enableAutoCleanup) {
      this.cleanupTimer = setInterval(() => {
        this.cleanupExpired();
        
        // Check memory usage and evict if necessary
        const stats = this.calculateStats();
        if (stats.memoryUsageMB > this.config.maxMemoryMB) {
          const entriesToEvict = Math.ceil(this.cache.size * 0.1); // Evict 10%
          this.evictEntries(entriesToEvict, EvictionStrategy.LRU);
        }
      }, this.config.cleanupInterval);
    }
  }

  /**
   * Stop automatic cleanup
   */
  private stopAutoCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }
}

